import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { PillButton } from "./PillButton";
import { scrollToId } from "../lib/lenis";
import { startCheckout } from "../lib/constants";

const NAV_LINKS = [
  { label: "What it is", target: "what-it-is", type: "scroll" },
  { label: "How it works", target: "/how-it-works", type: "route" },
  { label: "Pricing", target: "pricing", type: "scroll" },
  { label: "FAQ", target: "faq", type: "scroll" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      scrollToId(id);
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-cream/80 backdrop-blur-xl shadow-[0_1px_0_0_rgba(43,38,32,0.08)]"
            : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0 })}>
            <Logo height={52} />
          </Link>

          <div className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((l) =>
              l.type === "route" ? (
                <Link
                  key={l.label}
                  to={l.target}
                  data-testid={`nav-link-${l.target.replace(/\//g, "")}`}
                  className="text-sm font-medium text-ink/80 transition-colors hover:text-terracotta"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.label}
                  onClick={() => goToSection(l.target)}
                  data-testid={`nav-link-${l.target}`}
                  className="text-sm font-medium text-ink/80 transition-colors hover:text-terracotta"
                >
                  {l.label}
                </button>
              )
            )}
            <PillButton
              variant="dark"
              className="px-6 py-3"
              data-testid="nav-cta-create"
              onClick={startCheckout}
            >
              Create my audio
            </PillButton>
          </div>

          <button
            className="lg:hidden text-ink"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-cream/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-5 px-6 py-8">
              {NAV_LINKS.map((l) =>
                l.type === "route" ? (
                  <Link
                    key={l.label}
                    to={l.target}
                    onClick={() => setOpen(false)}
                    className="text-lg font-serif text-ink"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <button
                    key={l.label}
                    onClick={() => goToSection(l.target)}
                    className="text-left text-lg font-serif text-ink"
                  >
                    {l.label}
                  </button>
                )
              )}
              <PillButton variant="dark" onClick={startCheckout}>
                Create my audio
              </PillButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
